// Open side panel to select columns and set default value
// It populates the form data with the selected column values and default value

import { useRef, useState } from 'react';
import { useDrop } from 'react-dnd';
import { Box, Button, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { Check, CloseTwoTone, KeyboardArrowDownRounded, WarningAmber } from '@mui/icons-material';
import { fetchMinMax, fetchUniqueOptions } from '../../api/filters';
import { fontSize } from '../..';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import { MinMaxPayload } from '../../redux/Parameters/ParameterInterface';

const DragColumnsParameterData = ({
  propKey,
  datasetId,
  workspaceId,
  setLoadColumn,
  loadColumn,
  isRange,
  setFormData,
  setLoading,
  setAlert,
  formData,
  isDate,
  loadDefaultValue,
  columns,
  setColumns,
  tableData,
  setLoadFromColumn,
  setLoadDefaultValue,
  showWarning,
}: any) => {
  const { savedCalculations } = useSelector((state: RootState) => {
    return {
      savedCalculations: state.calculations.savedCalculations,
    };
  });

  const [{ isOver }, drop] = useDrop({
    accept: ['card', 'calculation'],
    drop: (item) => handleColumnsDrop(item),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });
  const [open, setOpen] = useState(false);

  // Returns columns.aggregationType if defaultValue matches aggregationType, else returns empty string
  const getAggregationTypeIfDefaultMatches = () => {
    if (!columns || !columns.aggregationType) return '';
    const aggType = columns.aggregationType;
    const defaultValue = formData.defaultValue;

    let expectedValue: any = '';
    if (aggType === 'avg') {
      const average = calculateAverage(loadColumn as unknown as number[]);
      expectedValue = formData.dataType === 'number' ? Math.round(average) : average.toFixed(2);
    } else if (aggType === 'min' || aggType === 'max') {
      if (isDate) {
        expectedValue =
          aggType === 'min' ? new Date(loadColumn[0]) : new Date(loadColumn[loadColumn.length - 1]);
      } else if (Array.isArray(loadColumn) && loadColumn.length > 0) {
        const numericValues = loadColumn
          .filter((val: any) => !isNaN(Number(val)) && val !== '(Blank)' && val !== '(Empty)')
          .map(Number);
        if (numericValues.length > 0) {
          expectedValue =
            aggType === 'min' ? Math.min(...numericValues) : Math.max(...numericValues);
        } else {
          expectedValue = '';
        }
      }
    } else if (aggType === 'first' || aggType === 'last') {
      const temp = loadColumn.filter((val: any) => val !== '(Blank)' && val !== '(Empty)' && val);
      expectedValue = aggType === 'first' ? temp[0] : temp[temp.length - 1];
    }
    if (formData.dataType === 'date') {
      if (columns.defaultValue === formData.defaultValueMin) {
        return 'min';
      } else if (columns.defaultValue === formData.defaultValueMax) {
        return 'max';
      }
      return '';
    }
    if (
      (typeof expectedValue === 'number' && Number(defaultValue) === expectedValue) ||
      (typeof expectedValue === 'string' && String(defaultValue) === expectedValue)
    ) {
      return columns.aggregationType;
    }
    return '';
  };

  const [agg, setAgg] = useState(() => getAggregationTypeIfDefaultMatches() || '');
  const anchorEl = useRef(null);

  // handle set default value from column by defualt value is min for numbers and dates and none for text
  const handleSetDefaultValueFromColumn = async (agg: string) => {
    setAgg(agg);
    var defaultVal: string | number | any = '';
    if (agg === 'avg') {
      const average = calculateAverage(loadColumn as unknown as number[]);
      if (formData.dataType === 'number') {
        setAlert({
          severity: 'info',
          message: `Default value is nearest integer of average: ${average.toFixed(2)}`,
          show: true,
        });
      }
      defaultVal = formData.dataType === 'number' ? Math.round(average) : average.toFixed(2);
      setFormData((prev: any) => ({
        ...prev,
        defaultValue: defaultVal,
      }));
    } else if (agg === 'min' || agg === 'max') {
      const { min, max } = await minMax();
      defaultVal = agg === 'min' ? min : max;
      setFormData((prev: any) => ({
        ...prev,
        defaultValue: defaultVal[0],
      }));
    } else if (agg === 'first' || agg === 'last') {
      const temp = loadColumn.filter((val: any) => val !== '(Blank)' && val !== '(Empty)' && val);
      defaultVal = agg === 'first' ? temp[0] : temp[temp.length - 1];
      setFormData((prev: any) => ({
        ...prev,
        defaultValue: defaultVal,
      }));
    }
    setOpen(false);
    setColumns((prev: any) => ({ ...prev, aggregationType: agg, defaultValue: defaultVal }));
  };

  // load min and max values from a column
  const minMax = async () => {
    const payload: MinMaxPayload = {
      tableId: columns.tableId,
      fieldName: columns.fieldname ?? columns.fieldName,
      dataType: columns.dataType === 'number' ? 'integer' : columns.dataType,
      filterOption: 'minMax',
      // timeGrain: columns.dataType === 'date' ? 'date' : 'year',
      timeGrain: columns.dataType === 'date' || columns.dataType === 'timestamp' ? 'date' : 'year',
    };

    if (columns?.calculatedFieldId && columns.has_calculation) {
      const calc = savedCalculations.find(
        (calculation) => calculation.uuid === columns.calculatedFieldId,
      );

      payload.isCalculatedField = columns.has_calculation;
      payload.calculatedField = [
        {
          calculatedFieldName: calc!.calculationInfo.calculatedFieldName ?? '',
          fields: calc!.calculationInfo.fields,
          flows: calc!.calculationInfo.flows,
          conditionFilters: calc!.calculationInfo.conditionFilters,
          overrided: calc!.calculationInfo.overrideCount > 0,
        },
      ];
    }

    const res = await fetchMinMax(tableData.connectionId, datasetId, workspaceId, payload);
    if (res.isError) {
      setLoading(false);
      setAlert({
        severity: 'error',
        message:
          res.responseStatusCode === 422
            ? 'Looks like the column does not exist in the dataset'
            : 'Something went wrong while setting the default values',
        show: true,
      });
      if (res.responseStatusCode === 422) {
        setColumns((prev: any) => ({
          ...prev,
          fieldName: '',
          tableId: '',
          dataType: '',
          originalDataType: '',
          aggregationType: '',
        }));
      }
    }
    return res.data;
  };

  // helper function to calculate average
  const calculateAverage = (data: number[]): number => {
    if (!Array.isArray(data) || data.length === 0) return 0;
    // array may contain null || undefined or blank/empty string
    const value = data.map(Number).filter((num) => !isNaN(num));
    if (value.length === 0) return 0;
    const sum = value.reduce((acc, num) => acc + num, 0);
    const avg = sum / value.length;
    const allIntegers = value.every((num) => Number.isInteger(num));
    return allIntegers && Number.isInteger(avg) ? avg : parseFloat(avg.toFixed(6));
  };

  // helper function to get aggregate options according to data type
  const aggr: any[] =
    formData.dataType === 'text'
      ? [
          {
            opt: 'First',
            id: 'first',
          },
          {
            opt: 'Last',
            id: 'last',
          },
        ]
      : formData.dataType === 'date'
        ? [
            {
              opt: 'Min',
              id: 'min',
            },
            {
              opt: 'Max',
              id: 'max',
            },
          ]
        : [
            {
              opt: 'Avg',
              id: 'avg',
            },
            {
              opt: 'Min',
              id: 'min',
            },
            {
              opt: 'Max',
              id: 'max',
            },
          ];

  // handle drop event to add column and load unique values
  const handleColumnsDrop = async (item?: any) => {
    const calculation = savedCalculations.find(
      (calc: any) => calc.name === item.fieldData?.fieldname || calc.uuid === item.boxUUID,
    );

    if (calculation) {
      if (calculation.isAggregated) {
        setAlert({
          severity: 'warning',
          message: "Can't drop aggregated calculations here",
          show: true,
        });
        return;
      }

      const isParameterized = Object.keys(calculation.flowParameters).find(
        (flow) => calculation.flowParameters[flow],
      );
      if (isParameterized) {
        setAlert({
          severity: 'warning',
          message: "Can't drop parameterized calculations here",
          show: true,
        });
        return;
      }
    }

    const column = item?.fieldData || columns;
    setLoading(true);
    if (
      formData.dataType === column.dataType ||
      (formData.dataType === 'date' && column.dataType === 'timestamp') ||
      ((formData.dataType === 'number' || formData.dataType === 'decimal') &&
        (column.dataType === 'integer' || column.dataType === 'decimal'))
    ) {
      setColumns({
        ...column,
        agg,
        fieldName: column.fieldname,
        originalDataType: column.dataType,
      });
      handleAddColumnData(column, item?.type, calculation);
    } else {
      setLoading(false);
      setAlert({
        severity: 'error',
        message: 'data type does not match',
        show: true,
      });
    }
  };

  // take column name,data type and other metadata and load unique values from that table column
  const handleAddColumnData = async (selectedColumn: any, type: string, calc?: any) => {
    const payload: any = {
      tableId: selectedColumn.tableId,
      fieldName: selectedColumn.fieldname,
      dataType: selectedColumn.dataType,
      filterOption: 'allValues',
      timeGrain:
        selectedColumn.dataType === 'date' || selectedColumn.dataType === 'timestamp'
          ? 'date'
          : 'year',
    };
    if (type === 'calculation' && calc) {
      const flows = calc.calculationInfo.flows;

      payload.isCalculatedField = true;
      payload.calculatedField = [
        {
          calculatedFieldName: calc.name,
          fields: calc.calculationInfo.fields,
          flows: flows,
          conditionFilters: calc.calculationInfo.conditionFilters,
          overrided: calc.calculationInfo.overrideCount > 0,
        },
      ];
      setColumns((prev: any) => ({ ...prev, calculatedFieldId: calc.uuid, has_calculation: true }));
    }

    const res = await fetchUniqueOptions(tableData.connectionId, datasetId, workspaceId, payload);
    if (res.isError) {
      setLoading(false);
      setAlert({
        severity: 'error',
        message:
          res.responseStatusCode === 422
            ? 'Looks like the column does not exist in the dataset'
            : 'Something went wrong while getting the unique values',
        show: true,
      });
    }
    let result = res.data;
    if (result) {
      result = result.map((val: any) => {
        const s = val === null || val === undefined ? '(Blank)' : String(val).trim();
        if (!s || s === '') return '(Empty)'; // empty string
        return s;
      });
    }
    const unique = Array.from(new Set(result)).sort((a, b) => {
      if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
      }
      return String(a).localeCompare(String(b));
    });
    setLoadColumn(unique);
    // console.log(unique);
    if (isDate) {
      setFormData((prev: any) => ({
        ...prev,
        min: unique[0],
        max: unique[unique.length - 1],
        defaultValue: unique[0],
        defaultValueMin: unique[0],
        defaultValueMax: unique[unique.length - 1],
      }));
      setAgg('min');
    } else if (isRange === 'range') {
      const min = unique.reduce((a: any, b: any) => Math.min(a, b));
      const max = unique.reduce((a: any, b: any) => Math.max(a, b));
      // console.log(min, max);
      setFormData((prev: any) => ({
        ...prev,
        min,
        max,
        defaultValue: min,
        defaultValueMin: min,
        defaultValueMax: max,
      }));
      setAgg('min');
    }
    if (formData.dataType === 'number' || formData.dataType === 'decimal') {
      // Filter out non-numeric values before calculating min and max
      const numericValues = unique
        .filter((val: any) => !isNaN(Number(val)) && val !== '(Blank)' && val !== '(Empty)')
        .map(Number);
      const min = Math.min(...numericValues);
      const max = Math.max(...numericValues);
      setFormData((prev: any) => ({
        ...prev,
        min,
        max,
        defaultValue: min,
      }));
      setAgg('min');
    }
    if (formData.dataType === 'text') {
      setFormData((prev: any) => ({
        ...prev,
        defaultValue: unique.filter((val: any) => val !== '(Blank)' && val !== '(Empty)' && val)[0],
      }));
      setAgg('first');
    }
    setLoading(false);
  };

  return (
    <Box
      style={{
        width: '15.625rem',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
      sx={{
        backgroundColor: 'white',
        height: '100%',
        borderLeft: '2px solid #e0e0e0',
        zIndex: 1000,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '35%',
          padding: '0.5rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            alignItems: 'start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'start',
              gap: '0.5rem',
              paddingY: '0.5rem',
              borderBottom: '1px solid #e0e0e0',
              width: '100%',
            }}
          >
            <span style={{ fontSize: fontSize.large }}>
              {loadDefaultValue ? 'Default Value' : 'Columns'}
            </span>
            {/* <span style={{ fontSize: '0.8rem' }}>
              {loadDefaultValue
                ? 'Select column to set default value'
                : ''}
            </span> */}
          </Box>
        </Box>

        <Box
          ref={!loadDefaultValue ? drop : null}
          sx={{
            marginY: '1rem',
            width: '100%',
            height: '84.5%',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.8rem',
            outline: isOver ? '1px dashed #2bb9bb' : 'none',
            backgroundColor: isOver ? 'rgba(43, 185, 187, 0.05)' : 'white',
            borderRadius: '4px',
            minHeight: '100px',
          }}
        >
          {columns && columns.fieldName ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'relative',
                padding: loadDefaultValue ? '0.2rem' : '0.5rem',
                paddingLeft: !loadDefaultValue ? '2rem' : '0.6rem',
                border: showWarning ? '0.2px solid #850000ff' : '0.2px solid #e0e0e0',
                borderRadius: '4px',
                cursor: 'pointer',
                boxShadow: '0px 0px 1px rgba(0, 0, 0, 0.5)',
                transition: 'border 0.2s',
                '&:hover': {
                  border: showWarning ? '0.2px solid #850000ff' : '1px solid #404040',
                  '& .close-button': {
                    opacity: 1,
                  },
                },
              }}
            >
              {!loadDefaultValue && (
                <IconButton
                  size="small"
                  className="close-button"
                  title="Remove Column"
                  sx={{
                    opacity: 0,
                    position: 'absolute',
                    left: '0.2rem',
                    padding: '2px',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.03)',
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setLoadColumn([]);
                    setColumns(null);
                    setFormData((prev: any) => ({
                      ...prev,
                      defaultValue: '',
                      min: '',
                      max: '',
                      step: '',
                      defaultValueMin: '',
                      defaultValueMax: '',
                      originalDataType: '',
                    }));
                  }}
                >
                  <CloseTwoTone fontSize="small" />
                </IconButton>
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  alignItems: 'center',
                  position: 'relative',
                }}
              >
                <span>{columns.displayname || columns.fieldName}</span>
                {showWarning && (
                  <div
                    style={{
                      position: 'absolute',
                      // right: loadDefaultValue ? '60px' : '50px',
                      right: loadDefaultValue ? '10px' : '5px',
                      top: loadDefaultValue ? '5px' : '1.55px',
                    }}
                  >
                    <Tooltip
                      title="This column is missing in the dataset"
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: '12px',
                          },
                        },
                      }}
                      placement="top"
                    >
                      <WarningAmber
                        style={{
                          color: '#850000ff',
                        }}
                        fontSize="small"
                      />
                    </Tooltip>
                  </div>
                )}
                {loadDefaultValue && (
                  <button
                    type="button"
                    className="buttonCommon columnDown"
                    onClick={(e: any) => {
                      e.stopPropagation();
                      setOpen(true);
                      anchorEl.current = e.currentTarget;
                    }}
                    title="Click for Menu Options"
                    style={{
                      visibility:
                        formData.displayType === 'calendar (range selection)' ||
                        formData.displayType === 'range (range selection)' ||
                        showWarning
                          ? 'hidden'
                          : 'visible',
                      paddingLeft: '1px',
                    }}
                    ref={anchorEl}
                  >
                    <span style={{ color: 'black', textTransform: 'capitalize' }}>
                      {agg || 'none'}
                    </span>
                    <KeyboardArrowDownRounded />
                  </button>
                )}
                {loadDefaultValue ? (
                  <Menu
                    open={open}
                    anchorEl={anchorEl.current}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    onClose={() => setOpen(false)}
                    sx={{
                      '& .MuiPaper-root': {
                        boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
                      },
                      padding: 0,
                      margin: 0,
                    }}
                  >
                    {aggr?.map((opt: any, idx: number) => (
                      <MenuItem
                        key={idx}
                        sx={{
                          width: '65px',
                          fontSize: fontSize.medium,
                          padding: '3px 10px',
                          display: 'flex',
                          justifyContent: 'flex-start',
                          gap: '8px',
                          '&:hover': {
                            backgroundColor: '#40404',
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSetDefaultValueFromColumn(opt.id);
                        }}
                      >
                        <Check
                          sx={{
                            visibility: opt.id === agg ? 'visible' : 'hidden',
                            padding: '2px',
                            marginLeft: '-5px',
                          }}
                        />
                        {opt.opt}
                      </MenuItem>
                    ))}
                  </Menu>
                ) : (
                  !showWarning && <span>{columns.dataType}</span>
                )}
              </Box>
            </Box>
          ) : (
            <div>
              {loadDefaultValue ? (
                <Typography>Please select a column before setting a default value</Typography>
              ) : (
                <>
                  <Typography>Drag and drop columns here</Typography>
                  <Typography>No columns selected</Typography>
                </>
              )}
            </div>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            alignItems: 'center',
            // justifyContent: 'start',
          }}
        >
          {loadDefaultValue && (
            <Typography
              sx={{
                fontSize: fontSize.small,
              }}
            >
              Note: add column before setting default value
            </Typography>
          )}
          <Button
            sx={{
              border: '1px solid #00bcd4',
              '&:hover': {
                backgroundColor: '#00bcd4',
                color: '#e0e0e0',
              },
              width: '100%',
            }}
            onClick={() => {
              setLoadFromColumn(false);
            }}
          >
            OK
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default DragColumnsParameterData;
